import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import Pane from 'components/Common/Pane'
import SecureWithDepositTagAndTooltip from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndTooltip'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'components/Luxkit/Button/TextButton'
import Divider from 'components/Luxkit/Divider'
import LineChartLineIcon from 'components/Luxkit/Icons/line/LineChartLineIcon'
import LineClockIcon from 'components/Luxkit/Icons/line/LineClockIcon'
import LineGlobeIcon from 'components/Luxkit/Icons/line/LineGlobeIcon'
import LineGuidedtoursIcon from 'components/Luxkit/Icons/line/LineGuidedtoursIcon'
import LineMapIcon from 'components/Luxkit/Icons/line/LineMapIcon'
import LineStarIcon from 'components/Luxkit/Icons/line/LineStarIcon'
import LineUserIcon from 'components/Luxkit/Icons/line/LineUserIcon'
import LineUserSquareIcon from 'components/Luxkit/Icons/line/LineUserSquareIcon'
import LineUsersAltIcon from 'components/Luxkit/Icons/line/LineUsersAltIcon'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import PriceRowCaption from 'components/Luxkit/PricePoints/PriceRowCaption'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowSale from 'components/Luxkit/PricePoints/PriceRowSale'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import Heading from 'components/Luxkit/Typography/Heading'
import TourV2ItineraryLocationList from 'components/OfferPage/TourV2/TourV2ItineraryLocationList'
import TourDiscountLabel from 'components/Tours/TourDiscountLabel'
import Group from 'components/utils/Group'
import useTourV2PurchasableOptionDetails from 'hooks/TourV2/useTourV2PurchasableOptionDetails'
import useTourV2VariationDetails from 'hooks/TourV2/useTourV2VariationDetails'
import { unique } from 'lib/array/arrayUtils'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { rem } from 'polished'
import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import TourV2GuideTypeToolTipInfo from './TourV2GuideTypeToolTipInfo'
import ContactContext from 'contexts/contactContext'

const OverviewPane = styled(Pane)`
  border-radius: ${props => props.theme.borderRadius.XS};
  border: 1px solid ${props => props.theme.palette.neutral.default.six};
  box-shadow: ${props => props.theme.shadow.flat.small};
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  padding: ${rem(20)};
`

const OverviewPaneDetailItemsContainer = styled.div`
  display: grid;
  gap: ${rem(8)};
  grid-auto-flow: row;
  grid-template-columns: repeat(2, 1fr);
`

const ContainerExtend = styled(Group)`
  grid-column: span 2;
`

interface Props {
  activityLevel: string | null;
  isPrivateRequestEnabled: boolean;
  purchasableOption?: Tours.TourV2OfferPurchasableOption;
  showSecureWithDepositTag?: boolean;
  hasSomeDiscount: boolean;
  allIsDiscounted: boolean;
  isOfferPurchasable: boolean;
  hasTourExperiences: boolean;
  offerType: Tours.V2OfferType;
  variation: Tours.TourV2OfferVariation;
  productType: Tours.V2ProductType;
  scrollToDepartures: () => void;
  guideType: string | null;
  offerId: string;
}

function TourV2OverviewPanel(props: Props) {
  const {
    variation,
    activityLevel,
    isPrivateRequestEnabled,
    purchasableOption,
    showSecureWithDepositTag,
    hasSomeDiscount,
    allIsDiscounted,
    offerType,
    isOfferPurchasable,
    hasTourExperiences,
    productType,
    scrollToDepartures,
    guideType,
    offerId,
  } = props
  const purchasableOptionDetails = useTourV2PurchasableOptionDetails(purchasableOption)
  const {
    placesCount,
    durationInDaysCount,
    countriesCount,
    maxPax,
    minAge,
    maxAge,
  } = useTourV2VariationDetails(variation)
  const { defaultContact } = useContext(ContactContext)

  const locationsVisited = useMemo(() => {
    return unique(variation.itinerary
      .flatMap(itinerary => itinerary.locationsVisitedDetails)
      .map(location => location.name))
  }, [variation.itinerary])

  const tourV2StartLocation = useMemo(() => [variation.startLocation || ''],
    [variation.startLocation],
  )

  return (
    <OverviewPane>
      <Heading variant="heading5">
        Overview
      </Heading>
      <OverviewPaneDetailItemsContainer>
        <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineClockIcon aria-hidden="true" />}>
          {durationInDaysCount}
        </BodyTextBlock>
        {placesCount && (
          <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineMapIcon aria-hidden="true" />}>
            {placesCount}
          </BodyTextBlock>
        )}
        {countriesCount && (
          <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineGlobeIcon aria-hidden="true" />}>
            {countriesCount}
          </BodyTextBlock>
        )}
        {maxPax && (
          <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineUsersAltIcon aria-hidden="true" />}>
            {maxPax} people max.
          </BodyTextBlock>
        )}
        {minAge && (
          <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineUserSquareIcon aria-hidden="true" />}>
            {!maxAge ? `${minAge}+ yrs` : `${minAge} - ${maxAge} yrs`}
          </BodyTextBlock>
        )}
        {activityLevel && (
          <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineChartLineIcon aria-hidden="true" />} format="titlecase" >
            {activityLevel}
          </BodyTextBlock>
        )}
        <ContainerExtend direction="vertical" gap={8}>
          {isPrivateRequestEnabled && (
            <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineUserIcon aria-hidden="true" />}>
              Private departures available
            </BodyTextBlock>
          )}
          {guideType && <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineGuidedtoursIcon aria-hidden="true" />} endIcon={<TourV2GuideTypeToolTipInfo guideType={guideType}/> }>
            {guideType}
          </BodyTextBlock>}
          {hasTourExperiences && <BodyTextBlock variant="medium" colour="neutral-two" startIcon={<LineStarIcon aria-hidden="true" />}>
            Optional experiences available
          </BodyTextBlock>}
          <TourV2ItineraryLocationList locationsVisited={locationsVisited} />
        </ContainerExtend>
      </OverviewPaneDetailItemsContainer>
      <Divider direction="horizontal" kind="secondary" />
      <div>
        {!!purchasableOptionDetails && (
          <VerticalSpacer gap={4}>
            <div>
              {purchasableOptionDetails.pricePerPerson && (
                <>
                  <LabelGroup >
                    {showSecureWithDepositTag && <SecureWithDepositTagAndTooltip offerType={offerType} />}
                    {allIsDiscounted && <TourDiscountLabel />}
                    {hasSomeDiscount && <TourDiscountLabel saleOnDepartures />}
                  </LabelGroup>
                  <PriceRowPriceCaption>From</PriceRowPriceCaption>
                  <LuxPlusPriceStack size="L" price={Number(purchasableOptionDetails.pricePerPerson)} saleUnit="person" memberPrice={Number(purchasableOptionDetails.memberPrice)} isTour/>
                </>
              )}
              {purchasableOptionDetails.hasDiscount && <PriceRowSale value={purchasableOptionDetails.fullPricePerPerson} />}
              {!!purchasableOptionDetails.roomTypeName && <PriceRowCaption>{purchasableOptionDetails.roomTypeName}</PriceRowCaption>}
              {!!purchasableOptionDetails.valuedAt && <PriceRowValueDiscountWithCaption
                size="M"
                originalValue={purchasableOptionDetails.valuedAt.price}
                discountPercentage={purchasableOptionDetails.valuedAt.savingPercentage}
              />}
            </div>
            <PriceRowAgentHubCommission
              offerId={offerId}
              size="L"
              productType={productType}
              offerLocations={tourV2StartLocation}
            />
          </VerticalSpacer>
        )}
        {!isOfferPurchasable && (
          <Heading variant="heading5" align="center">
            This offer has sold out
          </Heading>
        )}
      </div>
      <Group direction="vertical" gap={8}>
        {isOfferPurchasable && (
          <TextButton kind="primary" fit="full-width" onClick={scrollToDepartures}>
            Check Availability
          </TextButton>
        )}
        {!isOfferPurchasable && (
          <TextButton kind="secondary" to="/tours">
            Browse all tour offers
          </TextButton>
        )}
        <TextButton
          fit="full-width"
          kind="secondary"
          href={`tel:${defaultContact.number}`}
        >
          Call {defaultContact.humanReadable}
        </TextButton>
      </Group>
    </OverviewPane>
  )
}

export default TourV2OverviewPanel
